import { useState, useEffect } from 'react';
import { IProject, CreateProjectRequest, UpdateProjectRequest, PaginationResult, ProjectQuery, PaginationQuery, ProjectStatus, AspectRatio } from '@packages/global';
import { projectApi } from '../services/user/project.api';
import { useDialog } from './useDialog';
import { ProjectFormData } from '../components/projects/types';

export function useProjects() {
  const [projects, setProjects] = useState<IProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProject, setEditingProject] = useState<IProject | null>(null);
  const [formData, setFormData] = useState<ProjectFormData>({
    name: '',
    description: '',
    status: ProjectStatus.DRAFT,
    thumbnailUrl: '',
    llmModelId: '',
    llmVlModelId: '',
    text2imageModelId: '',
    image2videoModelId: '',
    text2videoModelId: '',
    keyframe2videoModelId: '',
    multiImage2videoModelId: '',
    imageEditModelId: '',
    text2speechModelId: '',
    videoLipsyncModelId: '',
    videoWidth: undefined,
    videoHeight: undefined,
    videoAspectRatio: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const { confirm } = useDialog();
  const [pagination, setPagination] = useState<PaginationResult>({
    page: 1,
    limit: 48,
    total: 0,
    totalPages: 0,
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });

  useEffect(() => {
    loadProjects({ search: searchTerm }, pagination.page, pagination.limit);
  }, [searchTerm, pagination.page]);

  const loadProjects = async (
    queryParams: Partial<ProjectQuery> = {},
    page: number = 1,
    limit: number = 48
  ) => {
    try {
      const queryData: PaginationQuery<ProjectQuery> = {
        query: {
          search: queryParams.search ?? searchTerm,
          ...queryParams
        },
        pagination: {
          page,
          limit,
          sortBy: 'createdAt',
          sortOrder: 'desc'
        }
      };
      const response = await projectApi.projectPageList(queryData);
      setProjects(response.items);
      setPagination((prev) => ({
        ...prev,
        page,
        limit,
        total: response.pagination.total,
        totalPages: response.pagination.totalPages
      }));
    } catch (error) {
      console.error('加载剧集失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingProject) {
        const updateData: UpdateProjectRequest = {
          ...formData,
          videoAspectRatio: formData.videoAspectRatio ? formData.videoAspectRatio as AspectRatio : undefined
        };
        await projectApi.updateProject(editingProject.id, updateData);
      } else {
        const createData: CreateProjectRequest = {
          ...formData,
          videoAspectRatio: formData.videoAspectRatio ? formData.videoAspectRatio as AspectRatio : undefined
        };
        await projectApi.createProject(createData);
      }
      resetForm();
      loadProjects();
    } catch (error) {
      console.error('保存剧集失败:', error);
    }
  };

  const handleEdit = (project: IProject) => {
    setEditingProject(project);
    setFormData({
      name: project.name,
      description: project.description,
      status: project.status,
      thumbnailUrl: project.thumbnailUrl || '',
      llmModelId: project.llmModelId || '',
      llmVlModelId: project.llmVlModelId || '',
      text2imageModelId: project.text2imageModelId || '',
      image2videoModelId: project.image2videoModelId || '',
      text2videoModelId: project.text2videoModelId || '',
      keyframe2videoModelId: project.keyframe2videoModelId || '',
      multiImage2videoModelId: project.multiImage2videoModelId || '',
      imageEditModelId: project.imageEditModelId || '',
      text2speechModelId: project.text2speechModelId || '',
      videoLipsyncModelId: project.videoLipsyncModelId || '',
      videoWidth: undefined,
      videoHeight: undefined,
      videoAspectRatio: project.videoAspectRatio || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    const confirmed = await confirm('确定要删除这个剧集吗？', '确认删除', 'danger');
    if (confirmed) {
      try {
        await projectApi.deleteProject(id);
        loadProjects({ search: searchTerm }, pagination.page, pagination.limit);
      } catch (error) {
        console.error('删除剧集失败:', error);
      }
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    if (!value) {
      loadProjects({}, 1, pagination.limit);
    }
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    loadProjects({}, 1, pagination.limit);
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingProject(null);
    setFormData({
      name: '',
      description: '',
      status: ProjectStatus.DRAFT,
      thumbnailUrl: '',
      llmModelId: '',
      llmVlModelId: '',
      text2imageModelId: '',
      image2videoModelId: '',
      text2videoModelId: '',
      keyframe2videoModelId: '',
      multiImage2videoModelId: '',
      imageEditModelId: '',
      text2speechModelId: '',
      videoLipsyncModelId: '',
      videoWidth: undefined,
      videoHeight: undefined,
      videoAspectRatio: ''
    });
  };

  const handlePageChange = (page: number) => {
    loadProjects({ search: searchTerm }, page, pagination.limit);
  };

  return {
    projects,
    loading,
    showForm,
    editingProject,
    formData,
    searchTerm,
    pagination,
    setFormData,
    setShowForm,
    handleSubmit,
    handleEdit,
    handleDelete,
    handleSearchChange,
    handleClearSearch,
    resetForm,
    handlePageChange
  };
}